<template>
  <div>
    <!-- Banner -->
    <b-row>
      <b-col cols="12">
        <b-card class="banner-autoeficacia text-white mb-2">
          <b-row align-v="center">
            <b-col md="4" class="d-flex align-items-center">
              <feather-icon icon="FileTextIcon" size="32" class="mr-1" />
              <h1 class="text-white mb-0">Painel de Autoeficácia</h1>
            </b-col>
            <b-col md="8">
              <b-form @submit.prevent="fetchReport">
                <b-input-group class="input-group-merge border-0 shadow-sm rounded-pill bg-white px-1">
                  <b-input-group-prepend is-text class="bg-transparent border-0">
                    <feather-icon icon="SearchIcon" class="text-muted" />
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filters.search"
                    placeholder="Buscar por Nome do Aluno"
                    class="border-0 shadow-none bg-transparent py-2"
                    @input="debouncedFetch"
                  />
                </b-input-group>
              </b-form>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filtros -->
    <b-row class="mb-2 px-1">
      <div class="d-flex flex-wrap gap-2">
        <b-form-select v-model="filters.anoLetivo" :options="options.anoLetivo" class="filter-select mr-1 mb-1" @change="fetchReport" />
        <b-form-select v-model="filters.municipio" :options="options.municipio" class="filter-select mr-1 mb-1" @change="fetchReport" />
        <b-form-select v-model="filters.bairro" :options="options.bairro" class="filter-select mr-1 mb-1" @change="fetchReport" />
        <b-form-select v-model="filters.escola" :options="options.escola" class="filter-select mr-1 mb-1" @change="fetchReport" />
        <b-form-select v-model="filters.turma" :options="options.turma" class="filter-select mr-1 mb-1" @change="fetchReport" />
        <b-button variant="outline-primary" size="sm" class="mb-1" @click="resetFilters">
          Limpar Filtros
        </b-button>
      </div>
    </b-row>

    <!-- Conteúdo Principal -->
    <b-tabs content-class="mt-1" pills class="autoeficacia-tabs">
      <!-- Aba Visão Geral -->
      <b-tab active>
        <template #title>
          <feather-icon icon="PieChartIcon" size="16" class="mr-50" />
          <span>Visão Geral</span>
        </template>
        
        <b-row>
          <!-- Gráfico de Score Geral -->
          <b-col lg="6">
            <b-card>
              <div class="mb-2 d-flex justify-content-between align-items-center">
                <div>
                  <h3 class="text-primary-green mb-0">Score geral por escola</h3>
                  <span class="text-muted">Média das Somas</span>
                </div>
                <div class="h2 text-primary-green font-weight-bolder" v-if="reportData.mediaGeral">
                  {{ reportData.mediaGeral.toFixed(1) }}
                </div>
              </div>
              
              <div class="chart-wrapper mt-3">
                <div class="y-axis-label text-muted">Score</div>
                <vue-apex-charts
                  type="bar"
                  height="300"
                  :options="barChartOptions"
                  :series="barChartSeries"
                />
              </div>
            </b-card>
          </b-col>

          <!-- Tabela Simplificada na Visão Geral -->
          <b-col lg="6">
            <b-card>
              <div class="mb-2">
                <h3 class="text-primary-green mb-0">Recentes</h3>
              </div>
              <b-table
                :items="reportData.resultadosPorAluno.slice(0, 10)"
                :fields="tableFields.filter(f => f.key !== 'schoolName')"
                responsive
                class="mt-1 autoeficacia-table"
              >
                <template #cell(autoeficacia)="data">
                  <span class="font-weight-bold text-center d-block text-primary-green">{{ (data.value || 0).toFixed(1) }}</span>
                </template>
              </b-table>
            </b-card>
          </b-col>
        </b-row>
      </b-tab>

      <!-- Aba Ranking por Aluno -->
      <b-tab>
        <template #title>
          <feather-icon icon="UserIcon" size="16" class="mr-50" />
          <span>Ranking por Aluno</span>
        </template>
        
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h3 class="text-primary-green mb-0">Ranking Geral de Alunos</h3>
          </div>

          <b-table
            :items="sortedStudents"
            :fields="rankingStudentFields"
            :per-page="perPage"
            :current-page="rankingStudentCurrentPage"
            responsive
            class="autoeficacia-table"
            hover
          >
            <template #cell(posicao)="data">
              <span class="font-weight-bold">#{{ (rankingStudentCurrentPage - 1) * perPage + data.index + 1 }}</span>
            </template>
            <template #cell(autoeficacia)="data">
              <b-badge pill variant="light-primary" class="px-1 py-50">
                {{ (data.value || 0).toFixed(1) }}
              </b-badge>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center mt-1">
            <span class="text-muted small">
              Mostrando {{ sortedStudents.length > 0 ? (rankingStudentCurrentPage - 1) * perPage + 1 : 0 }} a 
              {{ Math.min(rankingStudentCurrentPage * perPage, sortedStudents.length) }} de 
              {{ sortedStudents.length }} alunos
            </span>
            <b-pagination
              v-model="rankingStudentCurrentPage"
              :total-rows="sortedStudents.length"
              :per-page="perPage"
              class="mb-0"
            />
          </div>
        </b-card>
      </b-tab>

      <!-- Aba Ranking por Turma -->
      <b-tab>
        <template #title>
          <feather-icon icon="UsersIcon" size="16" class="mr-50" />
          <span>Ranking por Turma</span>
        </template>
        
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h3 class="text-primary-green mb-0">Ranking Geral de Turmas</h3>
          </div>

          <b-table
            :items="sortedClassrooms"
            :fields="rankingClassroomFields"
            :per-page="perPage"
            :current-page="rankingClassroomCurrentPage"
            responsive
            class="autoeficacia-table"
            hover
          >
            <template #cell(posicao)="data">
              <span class="font-weight-bold">#{{ (rankingClassroomCurrentPage - 1) * perPage + data.index + 1 }}</span>
            </template>
            <template #cell(average)="data">
              <b-badge pill variant="light-success" class="px-1 py-50">
                {{ (data.value || 0).toFixed(1) }}
              </b-badge>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center mt-1">
            <span class="text-muted small">
              Mostrando {{ sortedClassrooms.length > 0 ? (rankingClassroomCurrentPage - 1) * perPage + 1 : 0 }} a 
              {{ Math.min(rankingClassroomCurrentPage * perPage, sortedClassrooms.length) }} de 
              {{ sortedClassrooms.length }} turmas
            </span>
            <b-pagination
              v-model="rankingClassroomCurrentPage"
              :total-rows="sortedClassrooms.length"
              :per-page="perPage"
              class="mb-0"
            />
          </div>
        </b-card>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BFormInput, BInputGroup, BInputGroupPrepend, BFormSelect, BTable, BPagination, BForm, BButton, BTabs, BTab, BBadge,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import autoeficaciaService from '@/services/autoeficaciaService'
import _ from 'lodash'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BTable,
    BPagination,
    BForm,
    BButton,
    BTabs,
    BTab,
    BBadge,
    VueApexCharts,
  },
  data() {
    return {
      currentPage: 1,
      rankingStudentCurrentPage: 1,
      rankingClassroomCurrentPage: 1,
      perPage: 10,
      reportData: {
        mediaGeral: 0,
        mediasPorEscola: [],
        mediasPorTurma: [],
        resultadosPorAluno: [],
      },
      filters: {
        search: '',
        anoLetivo: null,
        municipio: null,
        bairro: null,
        escola: null,
        turma: null,
      },
      options: {
        anoLetivo: [{ value: null, text: 'Ano Letivo' }],
        municipio: [{ value: null, text: 'Município' }],
        bairro: [{ value: null, text: 'Bairro' }],
        escola: [{ value: null, text: 'Escola' }],
        turma: [{ value: null, text: 'Turma' }],
      },
      tableFields: [
        { key: 'studentName', label: 'Nome do Aluno' },
        { key: 'autoeficacia', label: 'Autoeficácia', class: 'text-center' },
        { key: 'turma', label: 'Turma' },
        { key: 'schoolName', label: 'Escola' },
      ],
      rankingStudentFields: [
        { key: 'posicao', label: '#' },
        { key: 'studentName', label: 'Nome do Aluno' },
        { key: 'autoeficacia', label: 'Autoeficácia', class: 'text-center' },
        { key: 'schoolName', label: 'Escola' },
        { key: 'turma', label: 'Turma' },
      ],
      rankingClassroomFields: [
        { key: 'posicao', label: '#' },
        { key: 'classroomName', label: 'Turma' },
        { key: 'average', label: 'Autoeficácia', class: 'text-center' },
        { key: 'schoolName', label: 'Escola' },
      ],
      barChartOptions: {
        chart: {
          toolbar: { show: false },
        },
        colors: ['#2C7A4B'],
        plotOptions: {
          bar: {
            columnWidth: '45%',
            borderRadius: 4,
            distributed: false,
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: val => val.toFixed(1),
          offsetY: -25,
          style: {
            fontSize: '12px',
            colors: ['#304758'],
          },
        },
        xaxis: {
          categories: [],
          position: 'bottom',
          axisBorder: { show: true, color: '#e0e0e0' },
          axisTicks: { show: true, color: '#e0e0e0' },
          labels: {
            style: {
              colors: '#6e6b7b',
              fontSize: '11px',
            },
          },
        },
        yaxis: {
          min: 0,
          labels: {
            formatter: val => val.toFixed(1),
            style: {
              colors: '#6e6b7b',
            },
          },
        },
        grid: {
          show: true,
          borderColor: '#f1f1f1',
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        fill: {
          opacity: 1,
        },
      },
      initialLoadDone: false,
    }
  },
  computed: {
    barChartSeries() {
      return [{
        name: 'Autoeficácia',
        data: this.reportData.mediasPorEscola.map(x => x.average),
      }]
    },
    sortedStudents() {
      return [...(this.reportData.resultadosPorAluno || [])].sort((a, b) => (b.autoeficacia || 0) - (a.autoeficacia || 0))
    },
    sortedClassrooms() {
      return [...(this.reportData.mediasPorTurma || [])].sort((a, b) => (b.average || 0) - (a.average || 0))
    },
  },
  watch: {
    'reportData.mediasPorEscola': {
      handler(val) {
        this.barChartOptions = {
          ...this.barChartOptions,
          xaxis: {
            ...this.barChartOptions.xaxis,
            categories: val.map(x => x.schoolName),
          },
        }
      },
      deep: true,
    },
  },
  methods: {
    debouncedFetch: _.debounce(function () {
      this.fetchReport()
    }, 500),

    async fetchReport() {
      try {
        const data = await autoeficaciaService.getReport(this.filters)
        this.reportData = data
        
        // Popula as opções dos filtros apenas no primeiro carregamento ou se os dados mudarem significativamente
        if (!this.initialLoadDone && data.resultadosPorAluno.length > 0) {
          this.populateFilterOptions(data.resultadosPorAluno)
          this.initialLoadDone = true
        }
      } catch (error) {
        console.error('Erro ao buscar relatório de autoeficácia:', error)
      }
    },

    populateFilterOptions(students) {
      const getUniques = (arr, key) => {
        const values = [...new Set(arr.map(x => x[key]).filter(x => x))].sort()
        return values.map(v => ({ value: v, text: v }))
      }

      this.options.anoLetivo = [{ value: null, text: 'Ano Letivo' }, ...getUniques(students, 'anoLetivo')]
      this.options.municipio = [{ value: null, text: 'Município' }, ...getUniques(students, 'city')]
      this.options.bairro = [{ value: null, text: 'Bairro' }, ...getUniques(students, 'neighborhood')]
      this.options.escola = [{ value: null, text: 'Escola' }, ...getUniques(students, 'schoolName')]
      this.options.turma = [{ value: null, text: 'Turma' }, ...getUniques(students, 'turma')]
    },

    resetFilters() {
      this.filters = {
        search: '',
        anoLetivo: null,
        municipio: null,
        bairro: null,
        escola: null,
        turma: null,
      }
      this.fetchReport()
    },
  },
  created() {
    this.fetchReport()
  },
}
</script>

<style scoped>
.banner-autoeficacia {
  background: linear-gradient(118deg, #5AB179, #2C7A4B);
  border: none;
  min-height: 120px;
  display: flex;
  align-items: center;
}

.text-primary-green {
  color: #2C7A4B;
}

.bg-primary-green {
  background-color: #2C7A4B !important;
}

.filter-select {
  width: auto;
  min-width: 140px;
  border-radius: 8px;
  border: 1px solid #d8d6de;
  color: #6e6b7b;
}

.chart-wrapper {
  position: relative;
  padding-left: 30px;
}

.y-axis-label {
  position: absolute;
  left: -40px;
  top: 50%;
  transform: rotate(-90deg) translateY(-50%);
  font-size: 11px;
  white-space: nowrap;
}

.autoeficacia-table ::v-deep thead th {
  background-color: #2C7A4B !important;
  color: white !important;
  text-transform: none;
  font-weight: 600;
  padding: 12px;
  border: none;
}

.autoeficacia-table >>> tbody td {
  border-color: #f3f2f7;
  color: #6e6b7b;
}

.rounded-pill {
  border-radius: 50rem !important;
}

.gap-2 {
  gap: 0.5rem;
}

.autoeficacia-tabs .nav-pills .nav-link {
  border-radius: 8px;
  font-weight: 500;
  color: #6e6b7b;
  background-color: #f8f8f8;
  margin-right: 10px;
  transition: all 0.2s ease-in-out;
}

.autoeficacia-tabs .nav-pills .nav-link.active {
  background-color: #2C7A4B !important;
  color: white !important;
  box-shadow: 0 4px 8px rgba(44, 122, 75, 0.3);
}

.autoeficacia-tabs .nav-pills .nav-link:hover:not(.active) {
  background-color: #e9ecef;
}

.autoeficacia-table {
  border-radius: 12px;
  overflow: hidden;
}

.badge-light-primary {
  background-color: rgba(90, 177, 121, 0.12);
  color: #2C7A4B !important;
}

.badge-light-success {
  background-color: rgba(44, 122, 75, 0.12);
  color: #2C7A4B !important;
}
</style>
